import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../stores/auth-context';
import { useMode } from '../stores/mode-context';
import type { Guest } from '../types/guest';

export function QrRegistrationPage() {
  const { directus_id } = useParams<{ directus_id: string }>();
  const { isAuthenticated } = useAuth();
  const { registrationEnabled, syncRegistration } = useMode();

  // Self-healing: if server rejects due to mode mismatch, sync local flag immediately
  function syncModeFromError(err: any) {
    const msg = String((err && err.message) || '');
    if (msg.indexOf('Registration is disabled') !== -1) syncRegistration(false);
    else if (msg.indexOf('not available during registration') !== -1) syncRegistration(true);
  }
  const [guest, setGuest] = useState<Guest | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(
    null,
  );
  const [confirming, setConfirming] = useState<'register' | 'dinner' | null>(null);

  useEffect(() => {
    if (!directus_id) return;
    api.user
      .get(directus_id)
      .then((res) => {
        if (res.guest) {
          setGuest(res.guest as Guest);
        } else {
          setNotFound(true);
        }
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [directus_id]);

  async function handleRegister() {
    if (!directus_id) return;
    setConfirming(null);
    try {
      const res = await api.user.register(directus_id);
      setMessage({ text: res.message, type: res.success ? 'success' : 'error' });
      if (res.success && guest) {
        setGuest({ ...guest, registered: true, times: new Date().toISOString() });
      }
    } catch (err: any) {
      syncModeFromError(err);
      setMessage({ text: err.message || 'Ошибка сервера', type: 'error' });
    }
  }

  async function handleDinner() {
    if (!directus_id) return;
    setConfirming(null);
    try {
      const res = await api.user.dinner(directus_id);
      setMessage({ text: res.message, type: res.success ? 'success' : 'error' });
      if (res.success && guest) {
        setGuest({ ...guest, dinner: true });
      }
    } catch (err: any) {
      syncModeFromError(err);
      setMessage({ text: err.message || 'Ошибка сервера', type: 'error' });
    }
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ct-bg">
        <div className="text-ct-text-muted">Загрузка...</div>
      </div>
    );
  }

  if (notFound || !guest) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ct-bg">
        <div className="rounded-xl bg-ct-surface border border-ct-border shadow-ct-sm p-8 text-center">
          <div className="text-4xl font-extrabold text-ct-text-muted">404</div>
          <div className="mt-2 text-ct-text-muted">Участник не найден</div>
        </div>
      </div>
    );
  }

  const canRegister =
    guest.pay_date && registrationEnabled && isAuthenticated && !guest.registered;
  const canMarkDinner = guest.pay_date && isAuthenticated && !guest.dinner && !registrationEnabled;

  return (
    <div className="flex min-h-screen items-center justify-center bg-ct-bg p-4 overflow-hidden">
      <div className="pointer-events-none fixed inset-0 overflow-hidden -z-10" aria-hidden="true">
        <div className="absolute -top-[20%] -right-[10%] h-[500px] w-[500px] rounded-full bg-gradient-to-br from-ct-primary/10 to-ct-secondary/10 blur-[150px]" />
        <div className="absolute -bottom-[15%] -left-[10%] h-[350px] w-[350px] rounded-full bg-gradient-to-br from-ct-secondary/8 to-ct-primary/8 blur-[120px]" />
      </div>

      <div className="w-full max-w-md rounded-xl bg-ct-surface border border-ct-border shadow-ct-sm p-8">
        <div className="text-center">
          <h1 className="text-2xl font-extrabold tracking-tight text-ct-text">
            {guest.l_name} {guest.f_name}
          </h1>
          {guest.p_name && (
            <p className="text-ct-text-muted">{guest.p_name}</p>
          )}
        </div>

        <div className="mt-6 space-y-3">
          <InfoRow label="Компания" value={guest.company} />
          <InfoRow label="Страна" value={guest.country} />
          <InfoRow label="Email" value={guest.email} />

          <div className="flex items-center justify-between rounded-lg bg-slate-50 px-4 py-2.5 border border-ct-border">
            <span className="text-sm text-ct-text-muted">Оплата</span>
            <span
              className={`rounded-full px-3 py-0.5 text-xs font-semibold ${
                guest.pay_date
                  ? 'bg-emerald-100 text-emerald-700'
                  : 'bg-red-100 text-red-700'
              }`}
            >
              {guest.pay_date ? 'Оплачено' : 'Не оплачено'}
            </span>
          </div>

          <div className="flex items-center justify-between rounded-lg bg-slate-50 px-4 py-2.5 border border-ct-border">
            <span className="text-sm text-ct-text-muted">Регистрация</span>
            <span
              className={`rounded-full px-3 py-0.5 text-xs font-semibold ${
                guest.registered
                  ? 'bg-emerald-100 text-emerald-700'
                  : 'bg-amber-100 text-amber-700'
              }`}
            >
              {guest.registered ? 'Зарегистрирован' : 'Не зарегистрирован'}
            </span>
          </div>

          <div className="flex items-center justify-between rounded-lg bg-slate-50 px-4 py-2.5 border border-ct-border">
            <span className="text-sm text-ct-text-muted">Ужин</span>
            <span
              className={`rounded-full px-3 py-0.5 text-xs font-semibold ${
                guest.dinner
                  ? 'bg-emerald-100 text-emerald-700'
                  : 'bg-gray-100 text-gray-600'
              }`}
            >
              {guest.dinner ? 'Отмечен' : 'Не отмечен'}
            </span>
          </div>
        </div>

        {message && (
          <div
            className={`mt-4 rounded-lg p-3 text-center text-sm border ${
              message.type === 'success'
                ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                : 'bg-red-50 text-red-600 border-red-200'
            }`}
          >
            {message.text}
          </div>
        )}

        <div className="mt-6 space-y-3">
          {guest.registered && (
            <div className="text-center text-sm text-ct-text-muted">
              Зарегистрирован: {guest.times ? new Date(guest.times).toLocaleString() : 'Неизвестно'}
            </div>
          )}

          {confirming === 'register' && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-center">
              <p className="mb-2 text-sm text-amber-800 font-semibold">Зарегистрировать участника?</p>
              <div className="flex justify-center gap-3">
                <button
                  onClick={handleRegister}
                  className="rounded-full bg-gradient-to-r from-ct-primary to-ct-secondary px-5 py-2 text-sm font-bold text-ct-on-primary shadow-ct-lg transition-all duration-200 hover:-translate-y-0.5 hover:shadow-ct-md active:scale-[0.98]"
                >
                  Да, зарегистрировать
                </button>
                <button
                  onClick={() => setConfirming(null)}
                  className="rounded-lg border border-ct-border bg-ct-surface px-5 py-2 text-sm font-semibold text-ct-text-muted transition-all duration-200 hover:bg-indigo-50 hover:text-ct-primary active:scale-[0.98]"
                >
                  Отмена
                </button>
              </div>
            </div>
          )}

          {canRegister && confirming !== 'register' && (
            <button
              onClick={() => setConfirming('register')}
              className="w-full rounded-full bg-gradient-to-r from-ct-primary to-ct-secondary py-2.5 text-sm font-bold text-ct-on-primary shadow-ct-lg transition-all duration-200 hover:-translate-y-0.5 hover:shadow-ct-md active:scale-[0.98]"
            >
              Зарегистрировать
            </button>
          )}

          {confirming === 'dinner' && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-center">
              <p className="mb-2 text-sm text-amber-800 font-semibold">Отметить посещение ужина?</p>
              <div className="flex justify-center gap-3">
                <button
                  onClick={handleDinner}
                  className="rounded-full bg-gradient-to-r from-emerald-500 to-emerald-600 px-5 py-2 text-sm font-bold text-white shadow-ct-lg transition-all duration-200 hover:-translate-y-0.5 hover:shadow-ct-md active:scale-[0.98]"
                >
                  Да, отметить ужин
                </button>
                <button
                  onClick={() => setConfirming(null)}
                  className="rounded-lg border border-ct-border bg-ct-surface px-5 py-2 text-sm font-semibold text-ct-text-muted transition-all duration-200 hover:bg-indigo-50 hover:text-ct-primary active:scale-[0.98]"
                >
                  Отмена
                </button>
              </div>
            </div>
          )}

          {canMarkDinner && confirming !== 'dinner' && (
            <button
              onClick={() => setConfirming('dinner')}
              className="w-full rounded-full bg-gradient-to-r from-emerald-500 to-emerald-600 py-2.5 text-sm font-bold text-white shadow-ct-lg transition-all duration-200 hover:-translate-y-0.5 hover:shadow-ct-md active:scale-[0.98]"
            >
              Отметить ужин
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  if (!value) return null;
  return (
    <div className="flex items-center justify-between rounded-lg bg-slate-50 px-4 py-2.5 border border-ct-border">
      <span className="text-sm text-ct-text-muted">{label}</span>
      <span className="text-sm font-semibold text-ct-text">{value}</span>
    </div>
  );
}
